import { Link, useParams } from "@tanstack/react-router";
import { ChevronRight, MapPinned, PackageIcon } from "lucide-react";
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { useShopInventorySummaryQuery } from "@/modules/merchant/presentation/api/queries";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SummaryCard } from "@/shared/ui/SummaryCard";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;
const MOVEMENTS_ROUTE = "/app/shops/$shopId/inventory/movements" as const;
const WAREHOUSE_REPORTS_ROUTE =
  "/app/shops/$shopId/reports/warehouse-locations" as const;

/**
 * P4 — ສະຫຼຸບສາງຈາກ API summary ທີ່ມີ; ລິ້ງໄປລາຍລະອຽດສາງ/ການເຄື່ອນໄຫວ
 */
export function ShopReportsInventoryPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);
  const showWarehouseReports = hasShopFeature(
    currentShop?.featureFlags,
    "warehouse_locations",
  );
  const summary = useShopInventorySummaryQuery(shopId);
  const numberFmt = useMemo(() => new Intl.NumberFormat("lo-LA"), []);
  const formatCount = (n: number | undefined) =>
    summary.isLoading ? "—" : numberFmt.format(n ?? 0);

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.inventory.read}
    >
      <Header>
        <Breadcrumb items={shopBreadcrumbs.reportsInventory(shopId)} />
      </Header>
      <Main>
        <div className="mb-6">
          <h1 className="font-heading font-semibold text-foreground text-xl">
            ລາຍງານສາງ
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ສະຫຼຸບຂໍ້ມູນສຕ໋ອກ — ເຂົ້າໜ້າສາງສຳລັບລາຍລະອຽດ SKU ແລະ ການເຄື່ອນໄຫວ
          </p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            title="ລວມ SKU ທັງໝົດ"
            value={formatCount(summary.data?.totalSkus)}
            description="ຈຳນວນ SKU ຂາຍໄດ້ທັງໝົດໃນຮ້ານ"
          />
          <SummaryCard
            title="ແຈ້ງເຕືອນສະຕ໋ອກຕໍ່າ"
            value={formatCount(summary.data?.lowStockAlerts)}
            description="ແຖວສະຕ໋ອກເຫຼືອ ≤ 5 ຊິ້ນຕໍ່ສາຂາ"
          />
          <SummaryCard
            title="ອັບເດດຫຼ້າສຸດ (7 ວັນ)"
            value={formatCount(summary.data?.recentUpdates)}
            description="ຈຳນວນແຖວສະຕ໋ອກທີ່ປ່ຽນໃນ 7 ວັນ"
          />
          <SummaryCard
            title="ສາຂາເປີດໃຊ້"
            value={formatCount(summary.data?.activeBranches)}
            description="ສາຂາສະຖານະເປີດໃນຮ້ານນີ້"
          />
        </div>

        {summary.isError ? (
          <p className="text-destructive text-sm">ໂຫຼດສະຫຼຸບສາງບໍ່ສຳເລັດ</p>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2 text-base">
              <PackageIcon className="size-5 text-primary" aria-hidden />
              ດຳເນີນການເພີ່ມເຕີມ
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              ເບິ່ງລາຍລະອຽດສິນຄ້າ, ສາງ, ແລະ ການເຄື່ອນໄຫວ
            </p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border rounded-lg border border-border">
              <li>
                <Link
                  to={INVENTORY_ROUTE}
                  params={{ shopId: shopId ?? "" }}
                  className="flex items-center gap-3 px-3 py-3 transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm">ລາຍການສຕ໋ອກ</p>
                    <p className="text-muted-foreground text-xs">ກັ່ນຕົວ, ປັບສະຕ໋ອກ</p>
                  </div>
                  <ChevronRight
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                </Link>
              </li>
              <li>
                <Link
                  to={MOVEMENTS_ROUTE}
                  params={{ shopId: shopId ?? "" }}
                  search={{
                    offset: 0,
                    limit: 20,
                    branchId: undefined,
                    shopProductSkuId: undefined,
                    type: undefined,
                    createdFrom: undefined,
                    createdTo: undefined,
                    recOnlyMismatch: false,
                  }}
                  className="flex items-center gap-3 px-3 py-3 transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm">
                      ການເຄື່ອນໄຫວສາງ
                    </p>
                    <p className="text-muted-foreground text-xs">ລາຍການເຄື່ອນໄຫວລ່າສຸດ</p>
                  </div>
                  <ChevronRight
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                </Link>
              </li>
              {showWarehouseReports ? (
                <li>
                  <Link
                    to={WAREHOUSE_REPORTS_ROUTE}
                    params={{ shopId: shopId ?? "" }}
                    className="flex items-center gap-3 px-3 py-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground text-sm">
                        ລາຍງານຕຳແໜ່ງສາງ
                      </p>
                      <p className="text-muted-foreground text-xs">
                        ຍັງບໍ່ຈັດຊ່ອງ, ຊ່ອງວ່າງ, GRN ຕາມຊ່ອງ
                      </p>
                    </div>
                    <MapPinned
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <ChevronRight
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                  </Link>
                </li>
              ) : null}
            </ul>
          </CardContent>
        </Card>
      </Main>
    </RequireMerchantPermission>
  );
}
